import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Group, Loader, Modal, Select, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { LoadMorePagination } from '@/components';
import { formsTemplatesService } from '@/services/formsTemplates.service';
import {
  useCreateFormTemplateTagMutation,
  useDeleteFormTemplateMutation,
  useDeleteFormTemplateTagMutation,
  useUpdateFormTemplateStatusMutation,
} from '@/hooks';
import { useFormTemplateTags, useFormTemplates } from '@/hooks/form-templates';
import type { FormTemplateDTO, FormTemplateListItemDTO, FormTemplateSortParams } from '@/types/formsTemplates';
import { TagFilters } from '../MessageTemplates/TagFilters/TagFilters';
import { TemplateCard } from './TemplateCard/TemplateCard';
import { FormBuilderModal } from './FormBuilderModal/FormBuilderModal';
import { FormPreviewModal } from './FormPreviewModal/FormPreviewModal';
import styles from './FormTemplates.module.sass';

type ModalState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; template: FormTemplateDTO }
  | { mode: 'loading'; templateId: string }
  | { mode: 'preview-loading'; templateId: string }
  | { mode: 'preview'; template: FormTemplateDTO };

const SORT_OPTIONS: { value: string; label: string; params: FormTemplateSortParams }[] = [
  { value: 'name-asc', label: 'A-Z', params: { sortBy: 'name', sortOrder: 'ASC' } },
  { value: 'name-desc', label: 'Z-A', params: { sortBy: 'name', sortOrder: 'DESC' } },
  { value: 'created-desc', label: 'Recent Added', params: { sortBy: 'created_date', sortOrder: 'DESC' } },
  { value: 'updated-desc', label: 'Recent Updated', params: { sortBy: 'updated_date', sortOrder: 'DESC' } },
  { value: 'created-asc', label: 'Earliest Added', params: { sortBy: 'created_date', sortOrder: 'ASC' } },
  { value: 'updated-asc', label: 'Earliest Updated', params: { sortBy: 'updated_date', sortOrder: 'ASC' } },
];

export const FormTemplates = () => {
  const [extraTemplates, setExtraTemplates] = useState<FormTemplateListItemDTO[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [modalState, setModalState] = useState<ModalState>({ mode: 'closed' });
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortValue, setSortValue] = useState<string>('created-desc');

  const sortParams = useMemo(() => SORT_OPTIONS.find((o) => o.value === sortValue)?.params, [sortValue]);

  const { data: tags = [], isLoading: isLoadingTags } = useFormTemplateTags();
  const { data: templatesData, isLoading: isLoadingTemplates } = useFormTemplates({
    sortBy: sortParams?.sortBy,
    sortOrder: sortParams?.sortOrder,
  });

  const createTagMutation = useCreateFormTemplateTagMutation();
  const deleteTagMutation = useDeleteFormTemplateTagMutation();
  const deleteTemplateMutation = useDeleteFormTemplateMutation();
  const updateStatusMutation = useUpdateFormTemplateStatusMutation();

  const firstPageTemplates = templatesData?.items ?? [];
  const total = templatesData?.total ?? 0;
  const templates = useMemo(() => [...firstPageTemplates, ...extraTemplates], [firstPageTemplates, extraTemplates]);
  const hasMore = templates.length < total;
  
  useEffect(() => {
    setExtraTemplates([]);
    if (firstPageTemplates.length > 0) {
      setCursor(firstPageTemplates[firstPageTemplates.length - 1].id);
    } else {
      setCursor(undefined);
    }
  }, [templatesData]);

  const handleLoadMore = useCallback(async () => {
    if (templates.length >= total || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const data = await formsTemplatesService.getFormTemplates({
        sortBy: sortParams?.sortBy,
        sortOrder: sortParams?.sortOrder,
        cursor,
      });
      if (data.items.length > 0) {
        setExtraTemplates((prev) => [...prev, ...data.items]);
        setCursor(data.items[data.items.length - 1].id);
      }
    } catch (error) {
      console.error('Failed to load more form templates:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [cursor, isLoadingMore, templates.length, total, sortParams]);

  const filteredTemplates = useMemo(() => {
    let result = templates;
    if (selectedTagIds.length > 0) {
      result = result.filter((t) => t.tags.some((tag) => selectedTagIds.includes(tag.id)));
    }
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      result = result.filter(
        (t) => t?.name?.toLowerCase().includes(query) || t?.description?.toLowerCase().includes(query)
      );
    }
    return result;
  }, [templates, selectedTagIds, searchQuery]);

  const tagCountMap = useMemo(() => {
    const map = new Map<string, number>();
    templates.forEach((t) => t.tags.forEach((tag) => map.set(tag.id, (map.get(tag.id) ?? 0) + 1)));
    return map;
  }, [templates]);

  const handleTagClick = (tagId: string | null) => {
    if (!tagId) {
      setSelectedTagIds([]);
      return;
    }
    setSelectedTagIds((prev) => (prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]));
  };

  const handleAddTag = (name: string) => {
    createTagMutation.mutate({ name });
  };

  const handleDeleteTag = (tagId: string) => {
    deleteTagMutation.mutate(tagId, {
      onSuccess: () => {
        setSelectedTagIds((prev) => prev.filter((id) => id !== tagId));
      },
    });
  };

  const handleTemplateCreated = () => {
    setModalState({ mode: 'closed' });
  };

  const handleTemplateUpdated = () => {
    setModalState({ mode: 'closed' });
  };

  const handleToggleStatus = async (templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;

    const newStatus = template.status === 'draft' ? 'published' : 'draft';
    setUpdatingStatusId(templateId);

    try {
      const fullTemplate = await formsTemplatesService.getFormTemplateById(templateId);
      updateStatusMutation.mutate(
        {
          id: templateId,
          payload: {
            status: newStatus,
            program: fullTemplate.program,
            content: fullTemplate.content,
          },
        },
        {
          onSettled: () => {
            setUpdatingStatusId(null);
          },
        }
      );
    } catch (error) {
      console.error('Failed to fetch template for status update:', error);
      setUpdatingStatusId(null);
    }
  };

  const handleEditTemplate = async (templateId: string) => {
    setModalState({ mode: 'loading', templateId });
    try {
      const fullTemplate = await formsTemplatesService.getFormTemplateById(templateId);
      setModalState({ mode: 'edit', template: fullTemplate });
    } catch (error) {
      console.error('Failed to fetch template for editing:', error);
      setModalState({ mode: 'closed' });
    }
  };

  const handlePreviewTemplate = async (templateId: string) => {
    setModalState({ mode: 'preview-loading', templateId });
    try {
      const fullTemplate = await formsTemplatesService.getFormTemplateById(templateId);
      setModalState({ mode: 'preview', template: fullTemplate });
    } catch (error) {
      console.error('Failed to fetch template for preview:', error);
      setModalState({ mode: 'closed' });
    }
  };

  const handleDeleteTemplate = (templateId: string) => {
    setDeleteConfirmId(templateId);
  };

  const handleConfirmDelete = () => {
    if (!deleteConfirmId) return;
    deleteTemplateMutation.mutate(deleteConfirmId, {
      onSuccess: () => {
        setDeleteConfirmId(null);
      },
    });
  };

  const isTagMutating = createTagMutation.isPending || deleteTagMutation.isPending;
  const isLoading = isLoadingTags || isLoadingTemplates;

  return (
    <Stack mx="xxl" py="md" gap="md">
      <Group align="center" justify="space-between">
        <Group align="baseline" gap="xs">
          <Title order={2}>Form Templates</Title>
          <Text c="dimmed" size="xl">
            {total}
          </Text>
        </Group>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setModalState({ mode: 'create' })}>
          Create Template
        </Button>
      </Group>

      <FormBuilderModal
        opened={modalState.mode === 'create' || modalState.mode === 'edit'}
        onClose={() => setModalState({ mode: 'closed' })}
        onCreated={handleTemplateCreated}
        onUpdated={handleTemplateUpdated}
        availableTags={tags}
        editTemplate={modalState.mode === 'edit' ? modalState.template : null}
      />

      <FormPreviewModal
        opened={modalState.mode === 'preview'}
        onClose={() => setModalState({ mode: 'closed' })}
        title={
          modalState.mode === 'preview' ? (modalState.template.content?.title ?? modalState.template.name ?? '') : ''
        }
        content={modalState.mode === 'preview' ? modalState.template.content : null}
      />

      <Modal
        opened={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        title="Delete Form Template"
        size="md"
        classNames={{
          header: styles.modalHeader,
          title: styles.modalTitle,
          close: styles.closeButton,
        }}
      >
        <Stack gap="md" mt="md">
          <Text size="sm">Are you sure you want to delete this template? This action cannot be undone.</Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button color="red" onClick={handleConfirmDelete} loading={deleteTemplateMutation.isPending}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      <TagFilters
        tags={tags}
        selectedTagIds={selectedTagIds}
        total={total}
        tagCountMap={tagCountMap}
        onTagClick={handleTagClick}
        onAddTag={handleAddTag}
        onDeleteTag={handleDeleteTag}
        disabled={isTagMutating}
      />

      <Group gap="sm" align="flex-end">
        <Select
          data={SORT_OPTIONS}
          value={sortValue}
          onChange={(value) => value && setSortValue(value)}
          allowDeselect={false}
          w={200}
        />
        <TextInput
          placeholder="Search templates by name..."
          leftSection={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          style={{ flex: 1 }}
        />
      </Group>

      <div className={styles.templatesGrid}>
        {isLoading && (
          <Stack align="center" justify="center" className={styles.loaderWrapper}>
            <Loader size="lg" />
          </Stack>
        )}

        {!isLoading && filteredTemplates.length === 0 && (
          <Stack align="center" justify="center" className={styles.emptyState}>
            <Text c="dimmed">No form templates found</Text>
          </Stack>
        )}

        {!isLoading &&
          filteredTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={handleEditTemplate}
              onDelete={handleDeleteTemplate}
              onPreview={handlePreviewTemplate}
              onToggleStatus={handleToggleStatus}
              isEditLoading={modalState.mode === 'loading' && modalState.templateId === template.id}
              isDeleteLoading={deleteTemplateMutation.isPending && deleteConfirmId === template.id}
              isStatusUpdating={updatingStatusId === template.id}
              isPreviewLoading={modalState.mode === 'preview-loading' && modalState.templateId === template.id}
            />
          ))}
      </div>

      <LoadMorePagination
        loaded={templates.length}
        total={total}
        hasMore={hasMore}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
      />
    </Stack>
  );
};
